/*
 * Copyright 2021 LABOR.digital
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Last modified: 2021.03.09 at 13:43
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { forEach, isFunction, isObject } from '@labor-digital/helferlein';
var BitRegistry = /** @class */ (function () {
    function BitRegistry(resolver) {
        this._bits = new Map();
        this._resolver = resolver;
    }
    /**
     * Registers a new bit instance for the specified type
     * @param type A type/unique identifier for this bit. This can be virtually any string.
     * @param ctor The bit class/constructor to use when the specified type was required
     */
    BitRegistry.prototype.add = function (type, ctor) {
        this._bits.set(type, ctor);
    };
    /**
     * Tries to resolve the given type and returns a constructor or null if the type could not be resolved.
     * @param type
     */
    BitRegistry.prototype.get = function (type) {
        if (!this._bits.has(type)) {
            return this.resolve(type);
        }
        return Promise.resolve(this._bits.get(type));
    };
    /**
     * Returns the map of all registered bit constructors by their type
     */
    BitRegistry.prototype.getAll = function () {
        return this._bits;
    };
    /**
     * Tries to find all types, that have been registered for the given constructor.
     * This will only search in loaded constructors! Dynamic constructors that are not yet resolved, will be ignored!
     * @param ctor
     */
    BitRegistry.prototype.getTypes = function (ctor) {
        var result = [];
        forEach(this._bits, function (bitCtor, type) {
            if (ctor === bitCtor) {
                result.push(type);
            }
        });
        return result;
    };
    /**
     * Internal helper that resolves a given bit type based on the registered dynamic resolver.
     * The result is either a bit constructor or null.
     *
     * The resolved bit (except null was resolved) will be injected into this._bits automatically,
     * so we can resolve the ctor faster in subsequent lookups
     *
     * @param type
     * @protected
     */
    BitRegistry.prototype.resolve = function (type) {
        return __awaiter(this, void 0, void 0, function () {
            var ctor, e_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        ctor = null;
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this._resolver(type)];
                    case 2:
                        ctor = _a.sent();
                        return [3 /*break*/, 4];
                    case 3:
                        e_1 = _a.sent();
                        throw new Error('Failed to resolve bit with type: "' + type + '". Error: ' + e_1.message);
                    case 4:
                        if (ctor === null) {
                            return [2 /*return*/, ctor];
                        }
                        ctor = this.translateResolved(ctor);
                        if (ctor === null) {
                            throw new Error('The bit resolver returned a invalid definition for: "' + type + '"!');
                        }
                        this._bits.set(type, ctor);
                        return [2 /*return*/, Promise.resolve(ctor)];
                }
            });
        });
    };
    /**
     * Internal helper to find the actual ctor in es modules when we used something like webpack
     * to import the module dynamically.
     * @param ctor
     * @protected
     */
    BitRegistry.prototype.translateResolved = function (ctor) {
        if (isObject(ctor) && ctor) {
            ctor = ctor;
            if (ctor.__bit) {
                return ctor;
            }
            if (ctor.__esModule) {
                if (ctor.default && ctor.default.__bit) {
                    return ctor.default;
                }
            }
            var found_1 = null;
            forEach(ctor, function (v) {
                if (v.__bit) {
                    found_1 = v;
                    return false;
                }
            });
            return found_1;
        }
        if (isFunction(ctor) && ctor.__bit) {
            return ctor;
        }
        return null;
    };
    return BitRegistry;
}());
export { BitRegistry };
//# sourceMappingURL=BitRegistry.js.map